import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { facescan } from "careplix-scan-sdk";
import { useClutch } from "./ClutchContext";

/* Compute composure from goals + the pulse measured under pressure */
function computeComposure({ goals, attempts, peakBpm }) {
  const goalRatio = attempts ? goals / attempts : 0;
  const goalPart = goalRatio * 50;
  const calmPart = Math.max(0, Math.min(50, (150 - peakBpm) / (150 - 88) * 50));
  const score = Math.round(Math.max(4, Math.min(100, goalPart + calmPart)));
  const stressPct = Math.round(Math.max(8, Math.min(99, (peakBpm - 70) / 80 * 100)));
  let verdict, blurb;
  if (score >= 80) { verdict = "Ice in the Veins"; blurb = "Clinical. Your heart barely flinched on sudden death."; }
  else if (score >= 50) { verdict = "Cool Head"; blurb = "Solid composure — a wobble or two, but you held it together."; }
  else { verdict = "Nerves of Glass"; blurb = "The pressure got you. Your pulse spiked when it mattered most."; }
  return { score, stressPct, verdict, blurb };
}

const PenaltyScan = () => {
  const navigate = useNavigate();
  const { config, details, setResult } = useClutch();
  const [analyzing, setAnalyzing] = useState(false);
  const analyzeMsg = "Reading your composure…";
  const [scanError, setScanError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  const videoRef = useRef();
  const canvasRef = useRef();
  const fieldRef = useRef();
  const ballRef = useRef();
  const keeperRef = useRef();
  const reticleRef = useRef();
  const powerRef = useRef();
  const powerFillRef = useRef();
  const ecgRef = useRef();
  const hudBpmRef = useRef();
  const hudStressRef = useRef();
  const clockRef = useRef();
  const scoreRef = useRef();
  const promptRef = useRef();
  const flashRef = useRef();
  const pipArcRef = useRef();
  const adTrackRef = useRef();
  const faceWarnRef = useRef();
  const loaderRef = useRef();

  // refs holding mutable game/vitals state (avoid re-renders in the RAF loop)
  const g = useRef({ phase: "idle", aimX: 0.1, aimDir: 1, power: 0, powerDir: 1, goals: 0, attempts: 0, busy: false });
  const v = useRef({ bpm: 78, targetBpm: 82, peakBpm: 78, wavePhase: 0, samples: [] });
  const clock = useRef({ elapsed: 0, mode: "sdk", startMs: 0, finished: false });
  const ecgBuf = useRef([]);

  const DUR = config.scanDurationSec;

  useEffect(() => {
    let raf;
    let finishedNav = false;
    let aborted = false;
    const timers = [];
    const later = (fn, ms) => { const id = setTimeout(fn, ms); timers.push(id); return id; };

    // fresh state for this (re)run — so "Try Again" starts a clean scan
    g.current = { phase: "idle", aimX: 0.1, aimDir: 1, power: 0, powerDir: 1, goals: 0, attempts: 0, busy: false };
    v.current = { bpm: 78, targetBpm: 82, peakBpm: 78, wavePhase: 0, samples: [] };
    clock.current = { elapsed: 0, mode: "sdk", startMs: 0, finished: false };
    ecgBuf.current = [];
    setScanError(null);
    setAnalyzing(false);
    if (loaderRef.current) loaderRef.current.style.display = "";
    if (faceWarnRef.current) faceWarnRef.current.style.display = "none";
    if (scoreRef.current) scoreRef.current.textContent = "0";

    /* ---------- finish handlers ---------- */
    const goResult = (vitals) => {
      if (finishedNav || aborted) return;
      finishedNav = true;
      clock.current.finished = true;
      const peak = Math.round(v.current.peakBpm);
      const realHr = vitals?.heart_rate ? Math.round(vitals.heart_rate) : peak;
      const comp = computeComposure({ goals: g.current.goals, attempts: g.current.attempts, peakBpm: realHr });
      setResult({
        ...comp,
        goals: g.current.goals,
        attempts: g.current.attempts,
        peakBpm: peak,
        vitals: vitals || null,
      });
      navigate("/result", { replace: true });
    };

    // Terminal failure — we NEVER fabricate vitals. Show a retry prompt instead of
    // navigating to a simulated result, so the result page only ever reflects a
    // real add-scan reading. Shares finishedNav with goResult (first one wins).
    const failScan = (msg) => {
      if (finishedNav || aborted) return;
      finishedNav = true;
      clock.current.finished = true;
      try { facescan.stopScan(); } catch (_) {}
      setAnalyzing(false);
      setScanError(msg || "We couldn't complete the scan. Please try again.");
    };

    /* ---------- real scan analysis ---------- */
    const analyzeRealScan = async ({ raw_intensity, ppg_time, average_fps }) => {
      if (aborted) return;
      setAnalyzing(true);
      clock.current.finished = true;
      // Safety net: never let the "analyzing" overlay hang. If the network calls
      // stall, surface a retry prompt (never a fabricated result).
      const analyzeWatchdog = later(() => failScan("We couldn't read your vitals in time. Please try again."), 50000);
      // POST + parse JSON with an abort timeout so a stalled request can't freeze us.
      const postJSON = async (url, body, ms) => {
        const ctrl = new AbortController();
        const id = setTimeout(() => ctrl.abort(), ms);
        try {
          const r = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
            signal: ctrl.signal,
          });
          return await r.json();
        } finally { clearTimeout(id); }
      };
      try {
        // create-token + add-scan are proxied through our Express server so the
        // api_key/secret come from runtime env (not baked into the CRA bundle)
        // and to avoid browser CORS to CarePlix. See server.js /api/careplix/*.
        const tokenJSON = await postJSON("/api/careplix/create-token", { employee_id: "demo-user" }, 12000);
        const scanToken = tokenJSON?.scan_token;
        if (!scanToken) throw new Error("token failed");

        const age = parseInt(details.age) || 28;
        const dob = `${new Date().getFullYear() - age}-01-01`;
        const addJSON = await postJSON("/api/careplix/add-scan", {
          scan_token: scanToken,
          posture: "resting",
          dob,
          gender: details.gender || "Other",
          metadata: {
            physiological_scores: { height: details.height || "170", weight: details.weight || "70" },
            ppg_time, raw_intensity,
            device: `RPPG_CAREPLIX_FACE_${/iPhone|iPad|iPod|Macintosh|Mac/i.test(navigator.userAgent) ? "IOS" : "ANDROID"}`,
            fps: average_fps,
          },
          notes: {
            client: config.scanClient, user_id: "", emp_name: details.name || "", age: details.age || "",
            gender: details.gender || "", height: details.height || "", weight: details.weight || "",
          },
        }, 30000);
        console.log("CarePlix add-scan →", addJSON?.statusCode, addJSON?.message || (addJSON?.vitals ? "vitals OK" : "no vitals"));
        const vitals = addJSON?.vitals;
        const ok2xx = addJSON?.statusCode != null && String(addJSON.statusCode).startsWith("2");
        const validHr = vitals && Number(vitals.heart_rate) > 0;
        if (ok2xx && validHr) {
          goResult({ ...vitals, wellness_score: addJSON.wellness_score, metadata: addJSON.metadata });
        } else throw new Error(addJSON?.message || "add-scan returned no vitals");
      } catch (err) {
        console.error("CarePlix analyze failed", err);
        failScan("We couldn't read your vitals. Please sit still in good light and try again.");
      } finally {
        clearTimeout(analyzeWatchdog);
      }
    };

    /* ---------- SDK setup ---------- */
    // Once the real SDK scan is genuinely producing frames, it is locked in: no
    // error (transient or otherwise) may switch to the simulated clock — we wait
    // for the real onScanFinish so the result comes from real measured vitals.
    let sdkProgressed = false;
    let faceOK = false;       // is a face currently detected by the SDK?
    let gameStarted = false;  // the first penalty only begins once the scan begins
    let framesSeen = false;   // has the camera produced ANY frame at all?
    let lastFrameMs = 0;      // timestamp of the most recent SDK frame (feed-stall watchdog)
    let sdkFinishWatch = null; // watchdog if the SDK hits full duration but never fires onScanFinish
    const stall = { elapsed: -1, atMs: 0 }; // tracks scan-time progress to detect a mid-scan freeze
    facescan.onFrame((fd) => {
      if (clock.current.mode !== "sdk") return;
      framesSeen = true;
      lastFrameMs = performance.now();
      // Camera is up the moment frames arrive → drop the "Opening camera…" loader.
      // The .cl-facewarn overlay then guides the user to look in until a face locks.
      if (loaderRef.current) loaderRef.current.style.display = "none";
      faceOK = fd.type === "scan"; // SDK reports "scan" only while a face is locked on
      if (fd.timeElapsed != null) {
        clock.current.elapsed = Math.min(DUR, fd.timeElapsed / 1000);
        if (clock.current.elapsed > 1) sdkProgressed = true;
      }
      // Scan has genuinely started → start the penalty shootout in lock-step
      if (faceOK) beginGame();
    });
    // The scan genuinely can't run (no camera / blocked / no internet). We never
    // fabricate vitals — surface an error and let the user retry, so the result
    // only ever reflects a real add-scan reading.
    const cameraFailed = () => {
      if (sdkProgressed || clock.current.finished) return;
      failScan("We couldn't start the camera scan. Please allow camera access, check your connection, and try again.");
    };

    facescan.onError((err, code) => {
      console.warn("CLUTCH SDK error", code, err?.message);
      const msg = err?.message || "";
      const fatal =
        code === "CMUSR01" || code === "FCINT01" ||
        /camera|internet|connection|network/i.test(msg);
      // Fatal → the scan can't produce real vitals, so we error out (no fabricated
      // result) even if it had already started — a dropped camera/network mid-scan is
      // terminal. Recoverable errors (face lost, movement) are ignored so the real SDK
      // scan continues uninterrupted.
      if (fatal) failScan("The camera scan stopped unexpectedly. Please allow camera access, check your connection, and try again.");
    });
    facescan.onScanFinish((data) => { analyzeRealScan(data); });

    /* Request the highest camera resolution the device supports so the SDK can
       detect the face reliably (mirrors the proven FaceScan integration). */
    let origGUM = null;
    try {
      origGUM = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
      navigator.mediaDevices.getUserMedia = function (constraints) {
        if (constraints?.video && !constraints?.audio && typeof constraints.video === "object") {
          // 720p is plenty for rPPG face-scan; 4K overloads mobile CPUs and causes
          // game lag + unreliable face detection. Cap framerate for steady frames.
          constraints.video.width = { ideal: 1280, min: 640 };
          constraints.video.height = { ideal: 720, min: 360 };
          constraints.video.frameRate = { ideal: 30, max: 30 };
        }
        return origGUM(constraints);
      };
    } catch (_) {}

    facescan
      .startScan({
        scanDuration: DUR,
        livelinessDetectionDuration: DUR,
        strictness: 0,
        videoElement: videoRef.current,
        canvasElement: canvasRef.current,
      })
      .catch((err) => {
        console.warn("startScan failed:", err?.message);
        cameraFailed();
      });

    // Restore the original getUserMedia after the SDK has grabbed its stream
    later(() => { try { if (origGUM) navigator.mediaDevices.getUserMedia = origGUM; } catch (_) {} }, 3000);

    // Safety watchdog: if the camera never produces ANY frame (silent init failure),
    // surface an error so the loader can't hang forever. A working camera that simply
    // hasn't locked a face yet keeps waiting (framesSeen guards it).
    later(() => { if (!framesSeen && !sdkProgressed) cameraFailed(); }, 6000);

    // Pre-progress watchdog: frames ARE arriving but the SDK never starts advancing
    // the clock (elapsed stays ≤1, so sdkProgressed never flips true). The mid-scan
    // freeze/feed guards are gated on sdkProgressed, so without this the user would
    // hang on /play forever with no result and no overlay. Surface a retry instead.
    later(() => { if (framesSeen && !sdkProgressed && !clock.current.finished) failScan("The scan couldn't start properly. Please try again."); }, 12000);

    /* ---------- build sponsor board: CarePlix / Loop logos in series ---------- */
    const t = adTrackRef.current;
    if (t) {
      t.innerHTML = "";
      const series = [
        { logo: "logo-careplix.png", cls: "cp" },
        { logo: "logo-loop.png", cls: "lp" },
        { logo: "logo-careplix.png", cls: "cp" },
        { logo: "logo-loop.png", cls: "lp" },
        { logo: "logo-careplix.png", cls: "cp" },
        { logo: "logo-loop.png", cls: "lp" },
      ];
      const build = () => series.forEach((s) => {
        const el = document.createElement("div");
        el.className = "cl-adslide " + s.cls;
        el.innerHTML = `<img src="${process.env.PUBLIC_URL}/${s.logo}" alt="" />`;
        t.appendChild(el);
      });
      build(); build(); // duplicate the series so the -50% loop is seamless
      t.animate([{ transform: "translateX(0)" }, { transform: "translateX(-50%)" }], { duration: 18000, iterations: Infinity });
    }

    /* ---------- game ---------- */
    const startKick = () => {
      const k = keeperRef.current, ball = ballRef.current;
      if (k) { k.className = "cl-keeper"; k.style.transform = "translateX(-50%)"; }
      if (ball) { ball.style.transition = "none"; ball.style.bottom = "14%"; ball.style.left = "50%"; ball.style.transform = "translateX(-50%)"; }
      reticleRef.current?.classList.remove("show");
      powerRef.current?.classList.remove("show");
      if (powerFillRef.current) powerFillRef.current.style.height = "0%";
      g.current.phase = "aim"; g.current.aimX = 0.1; g.current.aimDir = 1; g.current.busy = false;
      reticleRef.current?.classList.add("show");
      if (promptRef.current) { promptRef.current.style.display = "block"; promptRef.current.textContent = "TAP TO LOCK YOUR AIM"; }
    };

    // The very first penalty fires exactly when the real scan begins
    const beginGame = () => {
      if (gameStarted || aborted) return;
      gameStarted = true;
      if (loaderRef.current) loaderRef.current.style.display = "none";
      startKick();
    };

    const takeShot = () => {
      g.current.busy = true;
      const field = fieldRef.current;
      const fw = field.clientWidth;
      const goalLeft = 0.18, goalRight = 0.82;
      // The reticle (and ball) can travel beyond the posts, so a poorly-timed aim misses wide.
      const aimMin = 0.06, aimSpan = 0.88;
      const targetX = aimMin + g.current.aimX * aimSpan;
      const wide = targetX < goalLeft || targetX > goalRight;
      const zones = [0.3, 0.5, 0.7];
      const inGoal = Math.min(1, Math.max(0, (targetX - goalLeft) / (goalRight - goalLeft)));
      const aimZone = inGoal < 0.34 ? 0 : inGoal < 0.67 ? 1 : 2;
      let keeperZone = Math.floor(Math.random() * 3);
      if (Math.random() < 0.28) keeperZone = aimZone;
      const keeperX = zones[keeperZone];
      const over = g.current.power > 0.92;
      const weak = g.current.power < 0.14;
      const saved = !wide && !over && !weak && keeperZone === aimZone;

      const k = keeperRef.current;
      k.classList.add("dive");
      const dive = (keeperX - 0.5) * (fw * 0.6);
      k.style.transform = `translateX(-50%) translateX(${dive}px) rotate(${keeperX < 0.5 ? -12 : keeperX > 0.5 ? 12 : 0}deg)`;

      const ball = ballRef.current;
      // Ball always travels up to the goalpost: over the bar, a weak dribble, or on target
      const endBottom = over ? 70 : weak ? 20 : wide ? 56 : 50;
      ball.style.transition = "left .55s cubic-bezier(.3,.6,.4,1), bottom .55s cubic-bezier(.2,.5,.5,1), transform .55s";
      requestAnimationFrame(() => {
        ball.style.left = targetX * 100 + "%";
        ball.style.bottom = endBottom + "%";
        ball.style.transform = "translateX(-50%) scale(.5)"; // shrink for depth as it nears the goal
      });

      const isGoal = !over && !weak && !wide && !saved;
      v.current.targetBpm += clock.current.elapsed > DUR - 18 ? 11 : 6;

      later(() => {
        g.current.attempts++;
        if (isGoal) g.current.goals++;
        if (scoreRef.current) scoreRef.current.textContent = g.current.goals;
        const f = flashRef.current;
        if (f) {
          f.textContent = isGoal ? "GOAL!" : wide ? "WIDE!" : over ? "OVER!" : weak ? "WEAK!" : "SAVED!";
          f.style.color = isGoal ? "var(--cl-green)" : "var(--cl-coral)";
          f.classList.remove("go"); void f.offsetWidth; f.classList.add("go");
        }
        g.current.phase = "review";
        later(() => {
          if (clock.current.finished || clock.current.elapsed >= DUR - 1) { g.current.phase = "done"; if (promptRef.current) promptRef.current.style.display = "none"; }
          else startKick();
        }, 1100);
      }, 560);
    };

    const onTap = () => {
      if (clock.current.finished) return;
      if (clock.current.mode === "sdk" && !faceOK) return; // no face → can't take the penalty
      if (g.current.phase === "aim") {
        g.current.phase = "power";
        reticleRef.current?.classList.remove("show");
        powerRef.current?.classList.add("show");
        g.current.power = 0; g.current.powerDir = 1;
        if (promptRef.current) promptRef.current.textContent = "TAP FOR POWER & TIMING";
        v.current.targetBpm += 4;
      } else if (g.current.phase === "power") {
        g.current.phase = "shot";
        powerRef.current?.classList.remove("show");
        if (promptRef.current) promptRef.current.style.display = "none";
        takeShot();
      }
    };
    fieldRef.current?.addEventListener("click", onTap);

    // First kick is fired by beginGame() once the real scan actually starts.

    /* ---------- render loop ---------- */
    const ecg = ecgRef.current, ectx = ecg?.getContext("2d");
    const ecgWave = (ph) => {
      const x = (ph % (Math.PI * 2)) / (Math.PI * 2);
      let val = 0;
      val += 0.1 * Math.exp(-Math.pow((x - 0.15) / 0.03, 2));
      val -= 0.12 * Math.exp(-Math.pow((x - 0.27) / 0.012, 2));
      val += 1.0 * Math.exp(-Math.pow((x - 0.3) / 0.012, 2));
      val -= 0.22 * Math.exp(-Math.pow((x - 0.34) / 0.014, 2));
      val += 0.22 * Math.exp(-Math.pow((x - 0.55) / 0.05, 2));
      return val;
    };
    const colorFor = (b) => (b < 92 ? "#36e07f" : b < 112 ? "#ffc23d" : "#ff5a5f");
    let last = performance.now();

    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000); last = now;

      const elapsed = clock.current.elapsed;

      // pressure curve — BPM ramps, spikes in the final 18s
      if (!clock.current.finished) {
        const base = 82 + (elapsed / DUR) * 26;
        const spike = elapsed > DUR - 18 ? (elapsed - (DUR - 18)) * 1.6 : 0;
        const floor = base + spike;
        if (v.current.targetBpm < floor) v.current.targetBpm = floor;
        if (g.current.phase === "review") v.current.targetBpm -= (elapsed > DUR - 18 ? 8 : 16) * dt;
      } else {
        v.current.targetBpm -= 10 * dt;
      }
      v.current.targetBpm = Math.max(76, Math.min(155, v.current.targetBpm));
      v.current.bpm += (v.current.targetBpm - v.current.bpm) * Math.min(1, dt * 1.4) + Math.sin(now / 180) * 0.15;
      v.current.peakBpm = Math.max(v.current.peakBpm, v.current.bpm);
      if (!clock.current.finished) v.current.samples.push(v.current.bpm);
      v.current.wavePhase += dt * (v.current.bpm / 60) * Math.PI * 2;

      // ECG
      if (ectx) {
        ecgBuf.current.push(ecgWave(v.current.wavePhase));
        if (ecgBuf.current.length > ecg.width) ecgBuf.current.shift();
        const col = colorFor(v.current.bpm);
        ectx.clearRect(0, 0, ecg.width, ecg.height);
        ectx.strokeStyle = col; ectx.lineWidth = 2; ectx.lineJoin = "round";
        ectx.shadowColor = col; ectx.shadowBlur = 6; ectx.beginPath();
        const buf = ecgBuf.current, start = Math.max(0, buf.length - ecg.width);
        for (let i = start; i < buf.length; i++) {
          const x = i - start, y = ecg.height / 2 - buf[i] * (ecg.height / 2 - 3) * 1.4;
          if (i === start) ectx.moveTo(x, y); else ectx.lineTo(x, y);
        }
        ectx.stroke(); ectx.shadowBlur = 0;
      }

      // HUD
      if (hudBpmRef.current) { hudBpmRef.current.textContent = Math.round(v.current.bpm); hudBpmRef.current.style.color = colorFor(v.current.bpm); }
      if (hudStressRef.current) {
        const s = v.current.bpm < 92 ? "LOW" : v.current.bpm < 112 ? "RISING" : "PEAK";
        hudStressRef.current.textContent = s; hudStressRef.current.style.color = colorFor(v.current.bpm);
      }
      if (clockRef.current) clockRef.current.childNodes[0].textContent = String(Math.max(0, Math.ceil(DUR - elapsed)));

      // face-detection gate: block & warn when no face is in frame (real scan only)
      const faceMissing = clock.current.mode === "sdk" && !faceOK && !clock.current.finished;
      if (faceWarnRef.current) faceWarnRef.current.style.display = faceMissing ? "flex" : "none";
      if (promptRef.current && !clock.current.finished) {
        if (faceMissing) promptRef.current.style.display = "none";
        else if (g.current.phase === "aim") { promptRef.current.style.display = "block"; promptRef.current.textContent = "TAP TO LOCK YOUR AIM"; }
        else if (g.current.phase === "power") { promptRef.current.style.display = "block"; promptRef.current.textContent = "TAP FOR POWER & TIMING"; }
        else promptRef.current.style.display = "none";
      }

      // PIP scan ring
      if (pipArcRef.current) {
        const C = 201;
        pipArcRef.current.style.strokeDashoffset = C * (1 - elapsed / DUR);
      }

      // reticle sweep + power oscillate — frozen while the face isn't detected
      const canAct = clock.current.mode !== "sdk" || faceOK;
      if (g.current.phase === "aim" && canAct) {
        g.current.aimX += g.current.aimDir * dt * 0.85;
        if (g.current.aimX > 1) { g.current.aimX = 1; g.current.aimDir = -1; }
        if (g.current.aimX < 0) { g.current.aimX = 0; g.current.aimDir = 1; }
        const field = fieldRef.current;
        if (field && reticleRef.current) {
          const fw = field.clientWidth, fh = field.clientHeight;
          const tX = 0.06 + g.current.aimX * 0.88;
          reticleRef.current.style.left = tX * fw - 23 + "px";
          reticleRef.current.style.top = fh * 0.45 + "px";
          reticleRef.current.classList.toggle("miss", tX < 0.18 || tX > 0.82);
        }
      }
      if (g.current.phase === "power" && canAct) {
        g.current.power += g.current.powerDir * dt * 1.25;
        if (g.current.power > 1) { g.current.power = 1; g.current.powerDir = -1; }
        if (g.current.power < 0) { g.current.power = 0; g.current.powerDir = 1; }
        if (powerFillRef.current) powerFillRef.current.style.height = g.current.power * 100 + "%";
      }

      // SDK safety: the real scan reached full duration but onScanFinish hasn't
      // fired (rare SDK stall). Wait, then — ONLY if analysis never started — surface
      // a retry. The `!finished` guard is critical: analyzeRealScan sets
      // clock.finished the instant onScanFinish runs, so a slow create-token/add-scan
      // (60s of PPG data over a mobile network easily takes >10s) is NEVER aborted
      // here; analyzeWatchdog bounds the network calls instead.
      if (elapsed >= DUR && !clock.current.finished && !sdkFinishWatch) {
        sdkFinishWatch = later(() => { if (!clock.current.finished) failScan("The scan didn't finish in time. Please try again."); }, 15000);
      }

      // Mid-scan freeze guards apply ONLY during the live scan (elapsed < DUR). Once
      // the duration is reached, frames legitimately stop while we wait for
      // onScanFinish + add-scan — that window must never be aborted or we'd kill a
      // successful real result.
      if (!clock.current.finished && elapsed < DUR) {
        // frame/timer stall while a face is locked (no face → game paused, not a stall)
        if (!faceOK || elapsed > stall.elapsed + 0.05) {
          stall.elapsed = Math.max(stall.elapsed, elapsed);
          stall.atMs = now;
        } else if (sdkProgressed && now - stall.atMs > 14000) {
          failScan("The scan froze. Please try again.");
        }
        // silent camera-feed death (independent of face detection)
        if (sdkProgressed && lastFrameMs && now - lastFrameMs > 8000) {
          failScan("The camera feed stopped. Please try again.");
        }
      }

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const field = fieldRef.current;
    return () => {
      aborted = true;
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      field?.removeEventListener("click", onTap);
      try { if (origGUM) navigator.mediaDevices.getUserMedia = origGUM; } catch (_) {}
      try { facescan.stopScan(); } catch (_) {}
    };
  }, [retryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="cl-play" ref={fieldRef} style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/penalty-stadium.png)` }}>
      {/* SDK elements: video doubles as the live face-scan PIP */}
      <canvas ref={canvasRef} width={640} height={480} style={{ position: "fixed", top: -2000, left: -2000, width: 640, height: 480, opacity: 0, pointerEvents: "none" }} />

      <div className="cl-goal"><div className="net" /><div className="frame" /></div>
      <div className="cl-adboard"><div className="cl-adtrack" ref={adTrackRef} /></div>
      <div className="cl-keeper" ref={keeperRef}><img src={`${process.env.PUBLIC_URL}/keeper-mascot.png`} alt="Goalkeeper" /></div>
      <div className="cl-reticle" ref={reticleRef}><i /></div>
      <div className="cl-ball" ref={ballRef} />
      <div className="cl-power" ref={powerRef}><div className="sweet" /><div className="fill" ref={powerFillRef} /></div>

      {/* face-scan PIP */}
      <div className="cl-pip">
        <video ref={videoRef} autoPlay muted playsInline />
        <svg width="72" height="72" viewBox="0 0 72 72">
          <circle cx="36" cy="36" r="32" fill="none" stroke="rgba(255,255,255,.15)" strokeWidth="3" />
          <circle ref={pipArcRef} cx="36" cy="36" r="32" fill="none" stroke="var(--cl-accent2)" strokeWidth="3" strokeLinecap="round" strokeDasharray="201" strokeDashoffset="201" style={{ filter: "drop-shadow(0 0 4px var(--cl-accent2))" }} />
        </svg>
        <span className="lbl">● SCANNING</span>
      </div>

      {/* HUD */}
      <div className="cl-hud">
        <div className="bpm"><b ref={hudBpmRef} style={{ color: "#36e07f" }}>78</b><span>BPM</span></div>
        <canvas ref={ecgRef} width={200} height={40} />
        <div className="st"><span>STRESS <b ref={hudStressRef} style={{ color: "#dbe7f5" }}>LOW</b></span></div>
      </div>

      <div className="cl-scorebox"><b ref={scoreRef}>0</b><small>GOALS</small></div>
      <div className="cl-clock" ref={clockRef}>{DUR}<small>SECONDS</small></div>

      <div className="cl-prompt" ref={promptRef}>GET READY…</div>
      <div className="cl-flash" ref={flashRef} />

      <div className="cl-facewarn" ref={faceWarnRef}>
        <div className="ic">◠‿◠</div>
        <p>Look into the camera<small>No face detected — penalty paused</small></p>
      </div>

      <div className="cl-loader" ref={loaderRef}>
        <div className="cl-spin" />
        <p>Opening camera…<small>Get in frame to start the shootout</small></p>
      </div>

      {scanError && (
        <div className="cl-analyzing" style={{ padding: 24 }}>
          <div style={{ maxWidth: 360, textAlign: "center", background: "rgba(10,16,26,.92)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 18, padding: "28px 22px" }}>
            <div style={{ fontSize: 40, lineHeight: 1 }}>⚠️</div>
            <h3 style={{ fontFamily: "Anton, sans-serif", color: "#fff", fontSize: 22, margin: "12px 0 8px", letterSpacing: ".02em" }}>SCAN NOT COMPLETED</h3>
            <p style={{ fontFamily: "var(--cl-body)", color: "rgba(255,255,255,.75)", fontSize: 14, lineHeight: 1.5, margin: 0 }}>{scanError}</p>
            <button className="cl-btn" style={{ marginTop: 20 }} onClick={() => setRetryKey((k) => k + 1)}>Try Again</button>
            <button className="cl-btn ghost" style={{ marginTop: 10 }} onClick={() => navigate("/instructions")}>Back</button>
          </div>
        </div>
      )}

      {analyzing && (
        <div className="cl-analyzing">
          <div className="cl-spin" />
          <p style={{ fontFamily: "var(--cl-mono)", letterSpacing: ".14em", fontSize: 13, color: "rgba(255,255,255,.8)", textTransform: "uppercase" }}>{analyzeMsg}</p>
        </div>
      )}
    </div>
  );
};

export default PenaltyScan;
